/**
 * Kontaktinfo (owner, 2026-10-30): what members see on «Kontakt» in the app — the mosque's address, phone, e-mail and
 * website, and the people to contact with what they are («Imam», «Styreleder», «Kasserer»).
 *
 * GET /admin/contact-page gives the page and Brønnøysund's values (`official`); each empty field shows Brønnøysund's as
 * its placeholder, because that is what the app shows for it. «Lagre» is ONE PUT that replaces the whole page — the
 * fields and the list — in one transaction. Admin tier (settings.write), as «Nettside». The list: at most 20 people, in
 * the order they are shown, moved up and down here. Only people who have said yes to being shown (the note says so):
 * the page is public, and what is on it is anybody's to read.
 */
import React, { useEffect, useRef, useState } from 'react';
import { errSummary, fieldErrors, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { useDirty } from '../lib/nav';
import { Btn, Card, Field, FieldError, LoadError, Loading, Msg } from '../components/ui';
import { MosqueContact } from '../core';

type Profile = { address: string; phone: string; email: string; website: string };
type Person = { key: number; name: string; designation: string; phone: string; email: string };
const EMPTY_PROFILE: Profile = { address: '', phone: '', email: '', website: '' };
let nextKey = 1;
const toPerson = (p: Body): Person => ({ key: nextKey++, name: p.name || '', designation: p.designation || '', phone: p.phone || '', email: p.email || '' });
const toProfile = (p: Body | null | undefined): Profile => ({ address: (p && p.address) || '', phone: (p && p.phone) || '', email: (p && p.email) || '', website: (p && p.website) || '' });
/** What is sent: an empty field is null (the app then uses Brønnøysund's); the key is the panel's own. */
const toBody = (profile: Profile, people: Person[]) => ({
  profile: { address: profile.address || null, phone: profile.phone || null, email: profile.email || null, website: profile.website || null },
  people: people.map((p) => ({ name: p.name, designation: p.designation || null, phone: p.phone || null, email: p.email || null })),
});
/** Two drafts are the same page when they would send the same body. */
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** «Fra Brønnøysund: …» as the placeholder of an empty field, or nothing when Brønnøysund has none. */
const fromBrreg = (v: unknown) => (typeof v === 'string' && v ? `Fra Brønnøysund: ${v}` : '');

export function Kontaktinfo() {
  const { data, error, reload } = useLoad<Body>('/contact-page');
  const [profile, setProfile] = useState<Profile>(EMPTY_PROFILE);
  const [people, setPeople] = useState<Person[]>([]);
  const [saved, setSaved] = useState<ReturnType<typeof toBody> | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  useEffect(() => {
    if (!data) return;
    const p = toProfile(data.profile), l = (data.people || []).map(toPerson);
    setProfile(p); setPeople(l); setSaved(toBody(p, l));
  }, [data]);
  // The last flagged field put right: the «n felt må rettes» line by «Lagre» goes with it.
  const flagged = Object.keys(errors).length > 0;
  const wasFlagged = useRef(false);
  useEffect(() => { if (wasFlagged.current && !flagged) say(''); wasFlagged.current = flagged; }, [flagged, say]);
  const dirty = Boolean(saved) && !same(toBody(profile, people), saved);
  useDirty('contact-page', dirty);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;

  const max = Number(data.maxPeople) || MosqueContact.MAX_PEOPLE;
  const official: Body = data.official || {};
  const err = (k: string) => <FieldError text={errors[k]} />;
  // Polish pass 2026-10-30: a field's refusal goes when that field is edited (it stayed red after the fix until the next
  // «Lagre»), and the people's refusals go when the list is reordered — they are keyed by position.
  const unflag = (...keys: string[]) => setErrors((e) => (keys.some((k) => e[k]) ? Object.fromEntries(Object.entries(e).filter(([k]) => !keys.includes(k))) : e));
  const unflagPeople = () => setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith('people.'))));
  const editProfile = (k: keyof Profile, v: string) => { setProfile({ ...profile, [k]: v }); unflag('profile.' + k); };
  const setP = (i: number, patch: Partial<Person>) => {
    setPeople(people.map((p, j) => (j === i ? { ...p, ...patch } : p)));
    unflag(...Object.keys(patch).map((k) => `people.${i}.${k}`));
  };
  const move = (i: number, by: number) => {
    const to = i + by;
    if (to < 0 || to >= people.length) return;
    const next = people.slice();
    [next[i], next[to]] = [next[to], next[i]];
    setPeople(next); unflagPeople();
  };

  const save = async () => {
    const body = toBody(profile, people);
    // Core's own check first, so a mistake is named at its field before anything is sent; the server checks again.
    const pre = MosqueContact.input(body);
    if (!pre.ok) { setErrors(pre.errors); return say(errSummary({ errors: pre.errors }) || 'Sjekk feltene.', 'err'); }
    setBusy(true);
    const r = await send('/contact-page', { method: 'PUT', body: JSON.stringify(body) });
    setBusy(false);
    if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
    setErrors({});
    const p = toProfile(r.body.profile), l = (r.body.people || []).map(toPerson);
    setProfile(p); setPeople(l); setSaved(toBody(p, l));
    say(r.body.changed ? 'Lagret. Appen viser det nye innen fem minutter.' : 'Ingen endringer å lagre.', 'ok');
  };

  return (
    <>
      <Card title="Moskeens kontaktinfo" hint="Det medlemmene ser under «Kontakt» i appen. Et tomt felt viser verdien fra Brønnøysundregistrene, når den finnes.">
        <div className="card-body">
          <Field label="Adresse" note="For eksempel «Moskégata 1, 0123 Oslo». Kartet i appen bruker moskeens posisjon.">{(id, d) => <>
            <input id={id} aria-describedby={d} maxLength={MosqueContact.MAX_ADDRESS} autoComplete="off" value={profile.address}
              aria-invalid={Boolean(errors['profile.address']) || undefined} onChange={(e) => editProfile('address', e.target.value)} />{err('profile.address')}</>}</Field>
          <div className="row">
            <Field label="Telefon" className="field half" note="Siffer, mellomrom og + (5–20 tegn).">{(id, d) => <>
              <input id={id} aria-describedby={d} type="tel" inputMode="tel" maxLength={MosqueContact.PHONE_MAX} autoComplete="off" value={profile.phone}
                placeholder={fromBrreg(official.phone)} aria-invalid={Boolean(errors['profile.phone']) || undefined}
                onChange={(e) => editProfile('phone', e.target.value)} />{err('profile.phone')}</>}</Field>
            <Field label="E-post" className="field half">{(id) => <>
              <input id={id} type="email" maxLength={MosqueContact.MAX_EMAIL} autoComplete="off" value={profile.email}
                placeholder={fromBrreg(official.email)} aria-invalid={Boolean(errors['profile.email']) || undefined}
                onChange={(e) => editProfile('email', e.target.value)} />{err('profile.email')}</>}</Field>
          </div>
          <Field label="Nettside" note="https-adresse. «www.moskeen.no» blir https://www.moskeen.no.">{(id, d) => <>
            <input id={id} aria-describedby={d} type="url" inputMode="url" maxLength={MosqueContact.MAX_WEBSITE} autoComplete="off" value={profile.website}
              placeholder={fromBrreg(official.website)} aria-invalid={Boolean(errors['profile.website']) || undefined}
              onChange={(e) => editProfile('website', e.target.value)} />{err('profile.website')}</>}</Field>
          {data.official
            ? <p className="field note">Står et felt tomt, viser appen verdien fra Brønnøysundregistrene (vist i grått i feltet). Skriv inn noe for å bruke deres eget i stedet.</p>
            : <p className="field note">Ingen verdier fra Brønnøysundregistrene er knyttet til moskeen. Et tomt felt vises ikke i appen.</p>}
        </div>
      </Card>

      <Card title="Kontaktpersoner" hint={`Personer medlemmene kan kontakte, med stilling eller rolle. Vises i appen i denne rekkefølgen (høyst ${max}).`}>
        <div className="card-body">
          <p className="msg warn" role="note">Bare personer som har sagt ja til å vises i appen. Navn, rolle, telefon og e-post blir synlige for alle som åpner appen.</p>
          {!people.length && <p className="note">Ingen kontaktpersoner. Uten personer her eller i «Kontakter» viser appen IRNs kontaktperson for moskeen, hvis Masjid har slått den på.</p>}
          <ol className="contact-people" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {people.map((p, i) => (
              <li key={p.key} style={{ borderTop: i ? '1px solid var(--line, #e5e5e5)' : undefined, paddingTop: i ? 12 : 0, marginTop: i ? 12 : 0 }}>
                <div className="row">
                  <Field label={`Navn (person ${i + 1})`} required className="field half">{(id) => <>
                    <input id={id} maxLength={MosqueContact.MAX_NAME} autoComplete="off" value={p.name} aria-invalid={Boolean(errors[`people.${i}.name`]) || undefined}
                      onChange={(e) => setP(i, { name: e.target.value })} />{err(`people.${i}.name`)}</>}</Field>
                  <Field label="Stilling/rolle" className="field half" note="For eksempel «Imam», «Styreleder» eller «Kasserer».">{(id, d) => <>
                    <input id={id} aria-describedby={d} maxLength={MosqueContact.MAX_DESIGNATION} autoComplete="off" value={p.designation}
                      aria-invalid={Boolean(errors[`people.${i}.designation`]) || undefined} onChange={(e) => setP(i, { designation: e.target.value })} />{err(`people.${i}.designation`)}</>}</Field>
                </div>
                <div className="row">
                  <Field label="Telefon" className="field half">{(id) => <>
                    <input id={id} type="tel" inputMode="tel" maxLength={MosqueContact.PHONE_MAX} autoComplete="off" value={p.phone}
                      aria-invalid={Boolean(errors[`people.${i}.phone`]) || undefined} onChange={(e) => setP(i, { phone: e.target.value })} />{err(`people.${i}.phone`)}</>}</Field>
                  <Field label="E-post" className="field half">{(id) => <>
                    <input id={id} type="email" maxLength={MosqueContact.MAX_EMAIL} autoComplete="off" value={p.email}
                      aria-invalid={Boolean(errors[`people.${i}.email`]) || undefined} onChange={(e) => setP(i, { email: e.target.value })} />{err(`people.${i}.email`)}</>}</Field>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <Btn ghost disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Flytt ${p.name || `person ${i + 1}`} opp`}>Flytt opp</Btn>
                  <Btn ghost disabled={i === people.length - 1} onClick={() => move(i, 1)} aria-label={`Flytt ${p.name || `person ${i + 1}`} ned`}>Flytt ned</Btn>
                  <Btn ghost danger onClick={() => { setPeople(people.filter((_, j) => j !== i)); unflagPeople(); }} aria-label={`Fjern ${p.name || `person ${i + 1}`}`}>Fjern</Btn>
                </div>
              </li>
            ))}
          </ol>
          <div className="row" style={{ marginTop: 12, gap: 8 }}>
            <Btn ghost disabled={people.length >= max} onClick={() => setPeople([...people, toPerson({})])}>Legg til person</Btn>
            {people.length >= max && <span className="note">Høyst {max} personer.</span>}
          </div>
          {/* Owner 2026-10-30 (071): contacts ticked in «Kontakter» are listed after these, by name — said here always: the
              panel does not know whether that module is on, and the sentence is true either way. */}
          <p className="note" style={{ marginTop: 12 }}>Kontakter med «Vis i appen» i Kontakter vises også i appen, etter personene her.</p>
        </div>
      </Card>

      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <Btn small={false} disabled={busy || !dirty} onClick={save}>{busy ? 'Lagrer …' : 'Lagre'}</Btn>
        {dirty && <span className="note">Ulagrede endringer.</span>}
        <Msg text={msg.text} tone={msg.tone} />
      </div>
    </>
  );
}
